import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Upgrading to Gatsby v2 with the help of the hivemind 👌",
  "description": "",
  "published": "2018-06-26T00:00:00.000Z",
  "redirect_from": ["/blog/upgrading-gatsby-v2-hivemind/swizec/8481"],
  "categories": "Livecoding, Side Projects, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should `}<a parentName="em" {...{
          "href": "https://www.youtube.com/user/theswizec"
        }}>{`subscribe to ￼My Youtube￼ channel`}</a>{` to catch me live.`}</em></p>
    <iframe {...{
      "width": "100%",
      "height": 315,
      "src": "https://www.youtube-nocookie.com/embed/fG_SnnGnSXo?rel=0",
      "frameBorder": "0",
      "allow": "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture",
      "allowFullScreen": true
    }}></iframe>
    <p>{`You'd think upgrading Gatsby sites to `}<a parentName="p" {...{
        "href": "https://github.com/gatsbyjs/gatsby"
      }}>{`Gatsby 2.0`}</a>{` would be easy. The `}<a parentName="p" {...{
        "href": "https://next.gatsbyjs.org/docs/migrating-from-v1-to-v2/#update-layout-component"
      }}>{`update guide`}</a>{` is only 20 steps long.`}</p>
    <p>{`Change some package versions. Drop some old packages. Install some new. Tweak some code and you're off to the races.`}</p>
    <p>{`And then you get stuck.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Migrating to `}<a parentName="p" {...{
          "href": "https://twitter.com/GatsbyJS"
        }}>{`@gatsbyjs`}</a>{` v2 and getting stuck on this. What am I doing wrong `}<a parentName="p" {...{
          "href": "https://twitter.com/kylemathews"
        }}>{`@kylemathews`}</a>{` `}<a parentName="p" {...{
          "href": "https://twitter.com/swyx"
        }}>{`@swyx`}</a>{` `}<a parentName="p" {...{
          "href": "https://t.co/w48CH0fQrI"
        }}>{`pic.twitter.com/w48CH0fQrI`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1011018745349791744"
      }}>{`June 24, 2018`}</a></blockquote>
    <p>{`No error message. No nothing. Just stuck.`}</p>
    <p>{`Beta software, amirite?`}</p>
    <p>{`BUT! The internet hivemind comes to the rescue. The beauty of open source software. You do things out in the open, you help when you can, and you ask for help when you can't.`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/swyx"
      }}>{`@swyx`}</a>{` did an amazing thing and hunted down the source of my woes. It's a `}<a parentName="p" {...{
        "href": "https://github.com/gatsbyjs/gatsby/issues/6032"
      }}>{`GraphQL query compiler bug`}</a>{`.`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/ryanditjia"
      }}>{`@ryanditjia`}</a>{` was nice enough to submit a PR with a workaround. Thanks, Ryan and Shawn 🤘`}</p>
    <h2 {...{
      "id": "the-new-staticquery-system"
    }}>{`The new StaticQuery system`}</h2>
    <p>{`Gatsby v2 has a new `}<inlineCode parentName="p">{`StaticQuery`}</inlineCode>{` system, and it's got a bug.`}</p>
    <p>{`I'm not sure what `}<inlineCode parentName="p">{`StaticQuery`}</inlineCode>{` is supposed to do, or how, but `}<a parentName="p" {...{
        "href": "https://next.gatsbyjs.org/docs/migrating-from-v1-to-v2/#update-layout-component"
      }}>{`the migration guide`}</a>{` says you should use it to get info about your site. Title, description, stuff like that.`}</p>
    <p>{`I suspect you're meant to change all those `}<inlineCode parentName="p">{`export const query = graphql`}</inlineCode>{` queries into a `}<inlineCode parentName="p">{`StaticQuery`}</inlineCode>{`. Will have to experiment some more, read a doc or two 😇`}</p>
    <p>{`The easiest way to refactor your components to use StaticQuery goes like this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/components/layout.js

export default ({ children }) => (
  <staticquery query="{graphql\`" layoutquery="" {="" site="" sitemetadata="" title="" description="" }="" \`}="" render="{data" ==""> <layout data={data}>{children}</layout>}
  />
)
</staticquery>
`}</code></pre>
    <p>{`I used it for my `}<inlineCode parentName="p">{`Layout`}</inlineCode>{` component.`}</p>
    <p>{`StaticQuery takes a `}<inlineCode parentName="p">{`query`}</inlineCode>{` prop, which needs your graphql query. The stuff you used to `}<inlineCode parentName="p">{`export const query =`}</inlineCode>{`. And a `}<inlineCode parentName="p">{`render`}</inlineCode>{` prop, which takes the component you want to render once you have data.`}</p>
    <p>{`When Gatsby fixes the bug, you'll be able to make that code more readable like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`export default ({ children }) => (
  <staticquery query={query} render="{data" ==""> <layout data={data}>{children}</layout>}
  />
)
</staticquery>
`}</code></pre>
    <p>{`Beta software, amirite?`}</p>
    <h2 {...{
      "id": "a-tedious-gotcha-with-packages"
    }}>{`A tedious gotcha with packages`}</h2>
    <p>{`Another gotcha with the upgrade is the manual process to upgrade all your packages.`}</p>
    <p>{`You have to go into `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`, find everything that starts with `}<inlineCode parentName="p">{`gatsby-`}</inlineCode>{`, and change its version to `}<inlineCode parentName="p">{`next`}</inlineCode>{`. Then you have to hunt through the new `}<a parentName="p" {...{
        "href": "https://next.gatsbyjs.org/plugins/"
      }}>{`Gatsby Plugins Library`}</a>{` and see if anything you're using has new peer dependencies.`}</p>
    <p>{`You have to install peer dependencies yourself. I always forget that one.`}</p>
    <p>{`For example, Gatsby used to come with React packaged in. Now you have to install it yourself because it's a peer dependency.`}</p>
    <p>{`There's no telling which of the plugins you're using decided to pull similar tricks moving to Gatsby v2. Alas.`}</p>
    <p>{`I wish there was a code mod for that, but most people aren't running enough Gatsby sites to make the time savings worth it.`}</p>
    <h2 {...{
      "id": "conclusion-🤨"
    }}>{`Conclusion 🤨`}</h2>
    <p>{`Beta software, amirite?`}</p>
    <p>{`If you're running Gatsby v1, you should upgrade. Everything seems stable enough. `}<a parentName="p" {...{
        "href": "https://learnwhileyoupoop.com"
      }}>{`My site, Learn While You Poop`}</a>{`, is up and running. Ludicrously faster than it was before.`}</p>
    <p>{`Now I just gotta make the improvements I was planning to make before I got distracted by the upgrade 😅`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      